import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { BookOpenIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

import { PresentationCard } from '@restworld/ui-components';
import { AcademicCapIcon, UserAcademicCapIcon, toDateFormat } from '@restworld/utils-common';
import { WorkerCertificate } from '@restworld/utility-types';

export const CertificationSectionItem = ({
  workerCertificate,
}: {
  workerCertificate: WorkerCertificate;
  workerCertificateIndex: number;
}) => {
  const { t } = useTranslation();

  const memoValues = useMemo(() => {
    const certificateType = workerCertificate?.certificate_type_var.label;
    const courseName = workerCertificate?.course_name;
    const corporateName = workerCertificate?.corporate_name;
    const releaseDate = workerCertificate?.date ? toDateFormat(workerCertificate?.date) : undefined;
    return {
      certificateType,
      courseName,
      corporateName,
      releaseDate,
    };
  }, [
    workerCertificate?.certificate_type_var.label,
    workerCertificate?.corporate_name,
    workerCertificate?.course_name,
    workerCertificate?.date,
  ]);

  const { certificateType, courseName, corporateName, releaseDate } = memoValues;

  const errorMessage = t('common:missing_info');

  return (
    <div className="flex flex-col w-full">
      <div className="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
        <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
          <PresentationCard
            label={t('onboarding-page:type_of_course')}
            header={certificateType || errorMessage}
            icon={<AcademicCapIcon />}
            isError={!certificateType}
          />
          <PresentationCard
            label={t('onboarding-page:course_name')}
            header={courseName || errorMessage}
            isError={!courseName}
            icon={<BookOpenIcon className="w-6 h-6" />}
          />
        </div>
        <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
          <PresentationCard
            label={t('common:release_date')}
            header={releaseDate || errorMessage}
            isError={!releaseDate}
            icon={<UserAcademicCapIcon />}
          />
          <PresentationCard
            label={t('onboarding-page:issue_organization')}
            header={corporateName || errorMessage}
            isError={!corporateName}
            icon={<BuildingOfficeIcon className="w-6 h-6" />}
          />
        </div>
      </div>
    </div>
  );
};
